import { useState, useCallback, useEffect, useMemo } from 'react'
import { authFetch } from '../utils/authFetch'
import { readUrlFilters } from '../utils/urlFilters'
import { Company, SegmentOption, PlanOption } from '../types'

export interface CompanyFilters {
  segment: string | null
  planType: string | null
}

const EMPTY_FILTERS: CompanyFilters = { segment: null, planType: null }

interface UseCompaniesReturn {
  companies: Company[]
  filteredCompanies: Company[]
  segments: SegmentOption[]
  plans: PlanOption[]
  selectedCompany: Company | null
  filters: CompanyFilters
  hasActiveFilters: boolean
  selectionMode: 'company' | 'group' | null
  isLoading: boolean
  /** true depois da primeira carga de empresas (com a restauração de seleção
   *  via URL/localStorage já aplicada). Gate pra sincronização de URL não
   *  apagar parâmetros antes de a restauração acontecer. */
  isRestored: boolean
  error: string | null
  selectCompany: (company: Company | null) => void
  setFilter: (key: keyof CompanyFilters, value: string | null) => void
  clearFilters: () => void
  clearSelection: () => void
  searchCompanies: (query: string) => Company[]
  fetchCompanies: () => Promise<void>
}

function computeOptions<T extends string>(
  companies: Company[],
  filters: CompanyFilters,
  excludeKey: keyof CompanyFilters,
  getter: (c: Company) => T | null | undefined,
  sort?: (a: { name: T; count: number }, b: { name: T; count: number }) => number
): Array<{ name: T; count: number }> {
  // Apply all filters EXCEPT the one being computed
  let filtered = companies
  for (const [key, value] of Object.entries(filters)) {
    if (key === excludeKey || !value) continue
    filtered = filtered.filter(c => {
      const v = c[key as keyof Company]
      return typeof v === 'string' && v === value
    })
  }

  const map = new Map<T, number>()
  for (const c of filtered) {
    const val = getter(c)
    if (val) {
      map.set(val, (map.get(val) || 0) + 1)
    }
  }

  const result = Array.from(map.entries()).map(([name, count]) => ({ name, count }))
  if (sort) return result.sort(sort)
  return result.sort((a, b) => a.name.localeCompare(b.name))
}

export function useCompanies(): UseCompaniesReturn {
  const [companies, setCompanies] = useState<Company[]>([])
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null)
  // Filtros de grupo inicializam da URL (link compartilhável). A validação de
  // que o valor existe fica implícita: um segmento/plano inexistente só não
  // filtra nada — mesmo comportamento de sempre.
  const [filters, setFilters] = useState<CompanyFilters>(() => {
    const url = readUrlFilters()
    return { segment: url.segment, planType: url.planType }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [isRestored, setIsRestored] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some(v => v !== null),
    [filters]
  )

  const filteredCompanies = useMemo(() => {
    let result = companies
    if (filters.segment) result = result.filter(c => c.segment === filters.segment)
    if (filters.planType) result = result.filter(c => c.planType === filters.planType)
    return result
  }, [companies, filters])

  const segments = useMemo(
    () => computeOptions(companies, filters, 'segment', c => c.segment || null),
    [companies, filters]
  )

  const plans = useMemo(
    () => computeOptions(companies, filters, 'planType', c => c.planType || null),
    [companies, filters]
  )

  const selectionMode = selectedCompany ? 'company' : hasActiveFilters ? 'group' : null

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch('/api/companies')

      if (!response.ok) {
        throw new Error(`Erro ao buscar empresas: ${response.statusText}`)
      }

      const data = await response.json()
      const list: Company[] = Array.isArray(data) ? data : data.companies || []
      setCompanies(list)

      // Restaurar empresa selecionada: URL (link compartilhado) tem
      // precedência sobre o localStorage (última sessão local).
      const urlCompanyKey = readUrlFilters().companyKey
      const lastCompanyKey = urlCompanyKey || localStorage.getItem('selected_company_key')
      if (lastCompanyKey) {
        const company = list.find(c => c.key === lastCompanyKey)
        if (company) {
          setSelectedCompany(company)
        }
      }
    } catch (err: any) {
      console.error('Erro ao buscar empresas:', err)
      setError(err.message || 'Erro ao buscar empresas')
      setCompanies([])
    } finally {
      setIsLoading(false)
      setIsRestored(true)
    }
  }, [])

  const selectCompany = useCallback((company: Company | null) => {
    setSelectedCompany(company)
    if (company) {
      localStorage.setItem('selected_company_key', company.key)
      localStorage.setItem('shop_domain', company.domain)
    } else {
      localStorage.removeItem('selected_company_key')
    }
  }, [])

  const setFilterCb = useCallback((key: keyof CompanyFilters, value: string | null) => {
    setSelectedCompany(null)
    localStorage.removeItem('selected_company_key')
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS)
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedCompany(null)
    setFilters(EMPTY_FILTERS)
    localStorage.removeItem('selected_company_key')
  }, [])

  const searchCompanies = useCallback(
    (query: string): Company[] => {
      if (!query.trim()) return []
      const lowerQuery = query.toLowerCase()
      return filteredCompanies.filter(c =>
        c.title.toLowerCase().includes(lowerQuery) ||
        c.key.toLowerCase().includes(lowerQuery) ||
        c.domain.toLowerCase().includes(lowerQuery)
      )
    },
    [filteredCompanies]
  )

  useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  return {
    companies,
    filteredCompanies,
    segments,
    plans,
    selectedCompany,
    filters,
    hasActiveFilters,
    selectionMode,
    isLoading,
    isRestored,
    error,
    selectCompany,
    setFilter: setFilterCb,
    clearFilters,
    clearSelection,
    searchCompanies,
    fetchCompanies,
  }
}
